// mejora-web F4: biblioteca de vídeos (práctica / grabaciones de clases online).
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Plus, Edit2, Trash2, Loader2, AlertCircle, X, Upload, Link2, Film } from 'lucide-react';

import {
  listVideosAdmin, createVideo, updateVideo, deleteVideo, subirFicheroVideo, subirPortadaVideo,
  listProfesores, listClasesTipos,
} from '../../lib/adminApi';
import { ApiError } from '../../lib/api';
import CamposIdiomas from '../../components/admin/CamposIdiomas';

const TIPOS = ['practica', 'grabacion_clase'];
const VISIBILIDADES = ['membresia', 'publico'];
const FORM_VACIO = {
  titulo_es: '', titulo_en: '', titulo_ru: '', titulo_fr: '',
  descripcion_es: '', descripcion_en: '', descripcion_ru: '', descripcion_fr: '',
  tipo: 'practica', visibilidad: 'membresia', categoria: 'suave', duracion_minutos: '',
  fuente: 'storage', storage_path: '', url_externa: '', profesor_id: '', clase_tipo_id: '',
  fecha_grabacion: '', orden: 0, activo: true,
};

export default function AdminVideos() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editar, setEditar] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try { setItems((await listVideosAdmin()).data || []); }
    catch (e) { setError(e); } finally { setLoading(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const borrar = async (v) => {
    if (!window.confirm(t('admin.confirmar_borrar', { nombre: v.titulo_es }))) return;
    try { await deleteVideo(v.id); cargar(); } catch (e) { alert(e.message); }
  };

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <div>
          <h1>{t('admin.mw.videos.titulo')}</h1>
          <p className="admin-page-sub">{t('admin.mw.videos.sub')}</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setEditar(FORM_VACIO)}>
          <Plus size={16} strokeWidth={1.75} /> {t('admin.crear_nuevo')}
        </button>
      </header>

      {loading && <div className="admin-loading"><Loader2 size={24} className="spin" /></div>}
      {error && <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>}
      {!loading && !error && items.length === 0 && <p className="mw-vacio">{t('admin.mw.videos.vacio')}</p>}

      {!loading && !error && items.length > 0 && (
        <table className="admin-table">
          <thead>
            <tr>
              <th></th>
              <th>{t('admin.mw.videos.col.titulo')}</th>
              <th>{t('admin.mw.videos.col.tipo')}</th>
              <th>{t('admin.mw.videos.col.visibilidad')}</th>
              <th>{t('admin.mw.videos.col.fuente')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((v) => (
              <tr key={v.id} className={v.activo ? '' : 'is-inactivo'}>
                <td className="mw-thumb-cell">
                  {v.portada_url ? <img src={v.portada_url} alt="" className="mw-thumb" /> : <Film size={20} strokeWidth={1.2} />}
                </td>
                <td>{v.titulo_es}{!v.activo && <span className="estado-pill">{t('admin.mw.inactivo')}</span>}</td>
                <td>{t(`admin.mw.videos.tipo.${v.tipo}`)}</td>
                <td><span className={'estado-pill' + (v.visibilidad === 'publico' ? ' estado-pill--ok' : '')}>{t(`admin.mw.videos.visibilidad.${v.visibilidad}`)}</span></td>
                <td>{v.fuente === 'storage' ? t('admin.mw.videos.fuente.storage') : t('admin.mw.videos.fuente.externa')}</td>
                <td className="admin-acciones">
                  <button type="button" onClick={() => setEditar({ ...FORM_VACIO, ...v, duracion_minutos: v.duracion_minutos ?? '', profesor_id: v.profesor_id || '', clase_tipo_id: v.clase_tipo_id || '', fecha_grabacion: v.fecha_grabacion || '', url_externa: v.url_externa || '', storage_path: v.storage_path || '' })} aria-label={t('admin.editar')}>
                    <Edit2 size={16} strokeWidth={1.5} />
                  </button>
                  <button type="button" onClick={() => borrar(v)} aria-label={t('admin.borrar')}>
                    <Trash2 size={16} strokeWidth={1.5} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {editar && (
        <VideoModal item={editar} onCerrar={() => setEditar(null)} onGuardado={() => { setEditar(null); cargar(); }} />
      )}
    </div>
  );
}

function VideoModal({ item, onCerrar, onGuardado }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(item);
  const [fichero, setFichero] = useState(null);
  const [portada, setPortada] = useState(null);
  const [progreso, setProgreso] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [profesores, setProfesores] = useState([]);
  const [clases, setClases] = useState([]);
  const editando = !!item.id;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  useEffect(() => {
    listProfesores().then((r) => setProfesores(r.data || [])).catch(() => {});
    listClasesTipos().then((r) => setClases(r.data || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (form.fuente === 'storage' && !fichero && !form.storage_path) { setError(t('admin.mw.videos.falta_fichero')); return; }
    if (form.fuente === 'externa' && !/^https:\/\//.test(form.url_externa || '')) { setError(t('admin.mw.videos.falta_enlace')); return; }
    setEnviando(true);
    try {
      let storagePath = form.storage_path;
      if (form.fuente === 'storage' && fichero) {
        setProgreso(0);
        storagePath = await subirFicheroVideo(fichero, setProgreso);
      }
      const payload = {
        titulo_es: form.titulo_es, titulo_en: form.titulo_en, titulo_ru: form.titulo_ru, titulo_fr: form.titulo_fr,
        descripcion_es: form.descripcion_es, descripcion_en: form.descripcion_en,
        descripcion_ru: form.descripcion_ru, descripcion_fr: form.descripcion_fr,
        tipo: form.tipo, visibilidad: form.visibilidad,
        duracion_minutos: form.duracion_minutos === '' ? null : Number(form.duracion_minutos),
        fuente: form.fuente,
        storage_path: form.fuente === 'storage' ? storagePath : undefined,
        url_externa: form.fuente === 'externa' ? form.url_externa.trim() : undefined,
        profesor_id: form.profesor_id || null,
        clase_tipo_id: form.clase_tipo_id || null,
        fecha_grabacion: form.fecha_grabacion || null,
        orden: Number(form.orden) || 0,
        activo: !!form.activo,
      };
      const guardado = editando ? await updateVideo(item.id, payload) : await createVideo(payload);
      if (portada) await subirPortadaVideo(guardado.id, portada);
      onGuardado();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('admin.error_generico'));
    } finally { setEnviando(false); setProgreso(null); }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={() => !enviando && onCerrar()}>
      <div className="modal modal-admin" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="modal-cerrar" onClick={onCerrar} disabled={enviando}><X size={20} /></button>
        <h2 className="modal-titulo">{editando ? t('admin.editar') : t('admin.mw.videos.nuevo')}</h2>
        <form className="modal-form" onSubmit={submit}>
          <div className="admin-form-grid">
            <CamposIdiomas prefijo="titulo" etiqueta={t('admin.mw.videos.f.titulo')} form={form} setForm={setForm} requeridoEs />

            <div className="modal-campo">
              <label>{t('admin.mw.videos.col.tipo')}</label>
              <select value={form.tipo} onChange={set('tipo')}>
                {TIPOS.map((x) => <option key={x} value={x}>{t(`admin.mw.videos.tipo.${x}`)}</option>)}
              </select>
            </div>
            <div className="modal-campo">
              <label>{t('admin.mw.videos.col.visibilidad')}</label>
              <select value={form.visibilidad} onChange={set('visibilidad')}>
                {VISIBILIDADES.map((x) => <option key={x} value={x}>{t(`admin.mw.videos.visibilidad.${x}`)}</option>)}
              </select>
            </div>
            <div className="modal-campo">
              <label>{t('admin.mw.videos.f.duracion')}</label>
              <input type="number" min="1" max="600" value={form.duracion_minutos} onChange={set('duracion_minutos')} />
            </div>

            <div className="modal-campo full">
              <label>{t('admin.mw.videos.col.fuente')}</label>
              <div className="mw-radio">
                <label><input type="radio" name="fuente" checked={form.fuente === 'storage'} onChange={() => setForm((f) => ({ ...f, fuente: 'storage' }))} /> <Upload size={14} /> {t('admin.mw.videos.fuente.storage')}</label>
                <label><input type="radio" name="fuente" checked={form.fuente === 'externa'} onChange={() => setForm((f) => ({ ...f, fuente: 'externa' }))} /> <Link2 size={14} /> {t('admin.mw.videos.fuente.externa')}</label>
              </div>
            </div>
            {form.fuente === 'storage' ? (
              <div className="modal-campo full">
                <label>{t('admin.mw.videos.f.fichero')}</label>
                <input type="file" accept="video/mp4,video/webm,video/quicktime" onChange={(e) => setFichero(e.target.files[0] || null)} />
                <small className="mw-ayuda">
                  {form.storage_path && !fichero ? t('admin.mw.videos.fichero_actual') : t('admin.mw.videos.ayuda_fichero')}
                </small>
                {progreso !== null && (
                  <div className="mw-progreso"><span style={{ width: `${progreso}%` }} /><em>{progreso}%</em></div>
                )}
              </div>
            ) : (
              <div className="modal-campo full">
                <label>{t('admin.mw.videos.f.enlace')}</label>
                <input type="url" placeholder="https://youtu.be/… · https://vimeo.com/…" value={form.url_externa} onChange={set('url_externa')} />
                <small className="mw-ayuda">{t('admin.mw.videos.ayuda_enlace')}</small>
              </div>
            )}

            <div className="modal-campo full">
              <label>{t('admin.mw.videos.f.portada')}</label>
              {form.portada_url && <img src={form.portada_url} alt="" className="mw-modal-preview mw-modal-preview--sm" />}
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setPortada(e.target.files[0] || null)} />
            </div>

            <CamposIdiomas prefijo="descripcion" etiqueta={t('admin.mw.videos.f.descripcion')} form={form} setForm={setForm} multilinea />

            <div className="modal-campo">
              <label>{t('admin.mw.videos.f.profesora')}</label>
              <select value={form.profesor_id} onChange={set('profesor_id')}>
                <option value="">—</option>
                {profesores.map((p) => <option key={p.id} value={p.id}>{p.nombre_publico}</option>)}
              </select>
            </div>
            <div className="modal-campo">
              <label>{t('admin.mw.videos.f.clase')}</label>
              <select value={form.clase_tipo_id} onChange={set('clase_tipo_id')}>
                <option value="">—</option>
                {clases.map((c) => <option key={c.id} value={c.id}>{c.nombre_es}</option>)}
              </select>
            </div>
            {form.tipo === 'grabacion_clase' && (
              <div className="modal-campo">
                <label>{t('admin.mw.videos.f.fecha_grabacion')}</label>
                <input type="date" value={form.fecha_grabacion} onChange={set('fecha_grabacion')} />
              </div>
            )}
            <div className="modal-campo">
              <label>{t('admin.mw.f.orden')}</label>
              <input type="number" value={form.orden} onChange={set('orden')} />
            </div>
            <div className="modal-campo">
              <label className="mw-check"><input type="checkbox" checked={!!form.activo} onChange={set('activo')} /> {t('admin.mw.activo')}</label>
            </div>
          </div>

          {error && <div className="modal-error-general"><AlertCircle size={16} /><span>{error}</span></div>}
          <div className="modal-acciones">
            <button type="button" className="btn-secondary" onClick={onCerrar} disabled={enviando}>{t('comun.cancelar')}</button>
            <button type="submit" className="btn-primary" disabled={enviando}>
              {enviando ? <Loader2 size={16} className="spin" /> : t('comun.guardar')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
